<template>
  <div class="form-label">Creative</div>
  <div class="images-grid">
    <AdImagePicker
      v-for="banner in chosenBanners"
      :key="banner.id"
      v-bind="getChosenBannerState(banner)"
      boxSize
      @click="openPicker()"
      @remove="() => removeCreative(banner.banner_id || banner.id)" />
    <AdImagePicker :uploadText="$t('campaigns.choose_image')" boxSize @click="openPicker()" />
  </div>

  <div v-if="props.errors">
    <template v-for="(errorMessages, fieldName) in props.errors" :key="fieldName">
      <FormErrorList v-for="(message, index) in errorMessages" :key="`${fieldName}-${index}`" :errors="[message]" />
    </template>
  </div>

  <ImagePickerModal
    v-model="designerModal"
    :images="pickerImages"
    :selectedIds="bannersToSave"
    :title="$t('campaigns.choose_image')"
    :uploadText="$t('campaigns.choose_image')"
    @select="selectImageById" />
</template>

<script setup lang="ts">
import { FormErrorList } from '@prospective/lithium';
import { ref, computed, onMounted } from 'vue';
import type { IBanner } from '@/types/banner';
import { fetchOrderBanners } from '@/api/banner';
import { request } from '@/api/client';
import AdImagePicker from './AdImagePicker.vue';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';
import ImagePickerModal from './ImagePickerModal.vue';
import { bannerDisplayState, bannerThumbnailState } from '@/helpers/bannerState';

const getChosenBannerState = (bannerItem: any) => {
  const b = bannerItem.banner ?? bannerItem;
  return bannerDisplayState(b, b.render_image_id ?? null);
};

const props = withDefaults(
  defineProps<{
    modelValue: number[];
    providerId: string | null | undefined;
    errors?: Record<string, string[]>;
  }>(),
  {
    modelValue: () => [],
    errors: () => ({}),
  }
);

const designerModal = ref(false);
const banners = ref<IBanner[]>([]);

const pickerImages = computed(() =>
  banners.value.map((b) => {
    const state = bannerThumbnailState(b);
    return { id: b.id, ...state, disabled: !!b.render_error };
  })
);
const chosenBanners = ref();
const bannersToSave = ref<number[]>([]);
const providerStore = useProviderStore();

const { currentOrderId } = storeToRefs(providerStore);

const emit = defineEmits<{
  (e: 'update:modelValue', value: number[]): void;
}>();

onMounted(() => {
  request('GET', `/v1/account/campaigns/${props.providerId}/banners`).then(
    (res) => (
      (chosenBanners.value = res?.data || []),
      chosenBanners.value.forEach((banner: any) => {
        if (banner.banner_id) {
          bannersToSave.value.push(banner.banner_id);
        }
      }),
      emit('update:modelValue', bannersToSave.value)
    )
  );
});

const removeCreative = (bannerId: number) => {
  const index = chosenBanners.value.findIndex((banner: any) => {
    const id = banner.banner_id || banner.id;
    return id === bannerId;
  });
  if (index !== -1) {
    chosenBanners.value.splice(index, 1);
    bannersToSave.value = bannersToSave.value.filter((id) => id !== bannerId);
    emit('update:modelValue', bannersToSave.value);
  }
};

const openPicker = () => {
  designerModal.value = true;
  fetchOrderBanners(currentOrderId?.value ?? '').then((res) => {
    banners.value =
      res?.data?.filter((banner: IBanner) => {
        return !!banner.designer_template_id;
      }) || [];
  });
};

const selectImage = (banner: IBanner) => {
  const existingIndex = chosenBanners.value.findIndex((b: any) => (b.banner_id || b.id) === banner.id);

  if (existingIndex === -1) {
    chosenBanners.value.push(banner);
    bannersToSave.value.push(banner.id);
  } else {
    chosenBanners.value.splice(existingIndex, 1);
    bannersToSave.value = bannersToSave.value.filter((id) => id !== banner.id);
  }

  emit('update:modelValue', bannersToSave.value);
};

const selectImageById = (id: number) => {
  const banner = banners.value.find((b) => b.id === id);
  if (banner) selectImage(banner);
};
</script>

<style scoped lang="scss">
.form-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
}

.images-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-start;
}

.modal-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 10px;
  align-items: center;
}
</style>
